import { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, HelpCircle, RefreshCw, ClipboardCheck } from 'lucide-react';
import { CollapsibleCard } from '../../components/ui/collapsible-card';
import { api } from '../../core/apiClient';

interface CriterionResult {
  key: string;
  label: string;
  status: 'PASS' | 'FAIL' | 'WARNING' | 'UNKNOWN';
  tenderValue: string | null;
  threshold: string | null;
  reason: string;
}

const STATUS_CONFIG: Record<string, { icon: any; color: string; label: string }> = {
  PASS: { icon: CheckCircle2, color: 'text-green-600 bg-green-100', label: 'Passa' },
  FAIL: { icon: XCircle, color: 'text-red-600 bg-red-100', label: 'No passa' },
  WARNING: { icon: AlertTriangle, color: 'text-amber-600 bg-amber-100', label: 'Revisió manual' },
  UNKNOWN: { icon: HelpCircle, color: 'text-slate-500 bg-slate-100', label: 'Sense dades' },
};

export default function TenderEvaluationPanel({ tenderId }: { tenderId: string }) {
  const [results, setResults] = useState<CriterionResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvaluation = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<{ success: boolean; data: CriterionResult[] }>(`/tender-criteria/${tenderId}/evaluation`);
      setResults(res.data);
    } catch (err: any) {
      console.error('[TenderEvaluationPanel] Error avaluant la licitació:', err);
      setError(err.response?.data?.message || "Error en avaluar la licitació");
    } finally {
      setLoading(false);
    }
  }, [tenderId]);

  useEffect(() => {
    fetchEvaluation();
  }, [fetchEvaluation]);

  const passCount = results?.filter(r => r.status === 'PASS').length ?? 0;
  const failCount = results?.filter(r => r.status === 'FAIL').length ?? 0;

  return (
    <CollapsibleCard
      title="Avaluació segons criteris de valoració"
      icon={ClipboardCheck}
      iconClassName="text-purple-600"
      count={results?.length ?? 0}
      defaultOpen
      headerActions={
        <button
          type="button"
          onClick={fetchEvaluation}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-1.5 text-sm border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Actualitzar
        </button>
      }
    >
      <div className="p-5 space-y-3">
        {error && <p className="text-sm text-destructive">{error}</p>}

        {results && (
          <div className="flex items-center gap-4 text-sm pb-2">
            <span className="text-green-700">{passCount} criteris compleixen</span>
            <span className="text-red-700">{failCount} criteris no compleixen</span>
          </div>
        )}

        {results?.map((r) => {
          const cfg = STATUS_CONFIG[r.status];
          const Icon = cfg.icon;
          return (
            <div key={r.key} className="flex items-start gap-3 border rounded-lg p-3">
              <span className={`flex items-center justify-center h-7 w-7 rounded-full flex-shrink-0 ${cfg.color}`}>
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-sm">{r.label}</p>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{r.reason}</p>
                {(r.tenderValue || r.threshold) && (
                  <div className="flex flex-wrap gap-x-4 mt-1 text-xs">
                    {r.tenderValue && <span><strong>Detectat:</strong> {r.tenderValue}</span>}
                    {r.threshold && <span><strong>Configurat:</strong> {r.threshold}</span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {!loading && results?.length === 0 && (
          <p className="text-sm text-muted-foreground">No s'han pogut calcular els criteris.</p>
        )}
      </div>
    </CollapsibleCard>
  );
}
